import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, Bell, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import axios from 'axios';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, logoutUser } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user]);

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadNotifications();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'approval':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'info':
        return <Info className="h-4 w-4 text-blue-600" />;
      default:
        return <Bell className="h-4 w-4 text-slate-600" />;
    }
  };

  const handleLogout = () => {
    logoutUser();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 bg-[#8B1818] border-b-4 border-[#FDB913]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          {/* Logo - Much More Prominent */}
          <Link to="/" className="flex items-center space-x-4" data-testid="nav-logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_56e2863f-106a-4252-be25-54a4d7e6e68e/artifacts/8hjqi8mu_LDR_Logo.png" 
              alt="LexDoRight Logo" 
              className="h-20 w-auto"
            />
            <span className="text-3xl font-bold" style={{fontFamily: 'Playfair Display, serif'}}>
              <span className="text-[#FDB913]">Lex</span>
              <span className="text-white">DoRight</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-white hover:text-[#FDB913] font-medium transition-colors" data-testid="nav-home-link">Home</Link>
            <Link to="/applications" className="text-white hover:text-[#FDB913] font-medium transition-colors" data-testid="nav-applications-link">Applications</Link>
            <Link to="/products/ebooks" className="text-white hover:text-[#FDB913] font-medium transition-colors" data-testid="nav-ebooks-link">eBooks</Link>
            <Link to="/products/souvenirs" className="text-white hover:text-[#FDB913] font-medium transition-colors" data-testid="nav-souvenirs-link">Souvenirs</Link>
            <Link to="/forum" className="text-white hover:text-[#FDB913] font-medium transition-colors" data-testid="nav-forum-link">Forum</Link>
            <Link to="/blog" className="text-white hover:text-[#FDB913] font-medium transition-colors" data-testid="nav-blog-link">Blog</Link>
            <Link to="/faqs" className="text-white hover:text-[#FDB913] font-medium transition-colors" data-testid="nav-faqs-link">FAQs</Link>
          </div>

          {/* Right Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <>
                {user.is_admin && (
                  <Link to="/admin" data-testid="nav-admin-link">
                    <Button size="sm" className="bg-[#FDB913] text-[#8B1818] font-semibold hover:bg-[#e5a811] shadow-md">Admin</Button>
                  </Link>
                )}
                {/* Notifications */}
                <div className="relative">
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="relative text-white hover:text-[#FDB913] transition-colors p-2"
                    data-testid="nav-notifications-button"
                  >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-[#FDB913] text-[#8B1818] text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  
                  {/* Notifications Dropdown */}
                  {showNotifications && (
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-slate-200 z-50">
                      <div className="p-4 border-b border-slate-200 flex justify-between items-center">
                        <h3 className="font-bold text-slate-900">Notifications</h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-xs text-[#8B1818] hover:underline"
                          >
                            Mark all as read
                          </button>
                        )}
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="p-4 text-center text-slate-500">
                            No notifications yet
                          </div>
                        ) : (
                          notifications.slice(0, 10).map((notif) => (
                            <div
                              key={notif.id}
                              onClick={() => markAsRead(notif.id)}
                              className={`p-4 border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${!notif.read ? 'bg-blue-50' : ''}`}
                            >
                              <div className="flex items-start gap-3">
                                {getNotificationIcon(notif.notification_type)}
                                <div className="flex-1">
                                  <p className={`text-sm ${!notif.read ? 'font-semibold' : ''} text-slate-900`}>
                                    {notif.message}
                                  </p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {new Date(notif.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                                {!notif.read && (
                                  <span className="h-2 w-2 bg-[#8B1818] rounded-full flex-shrink-0"></span>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <Link to="/cart" className="relative" data-testid="nav-cart-link">
                  <ShoppingCart className="h-6 w-6 text-white hover:text-[#FDB913] transition-colors" />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#FDB913] text-[#8B1818] text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" data-testid="cart-count-badge">
                      {cartCount}
                    </span>
                  )}
                </Link>
                <Button onClick={handleLogout} variant="ghost" size="sm" className="text-white hover:text-[#FDB913] hover:bg-white/10" data-testid="nav-logout-button">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link">
                  <Button variant="ghost" size="sm" className="text-white hover:text-[#FDB913] hover:bg-white/10">Login</Button>
                </Link>
                <Link to="/register" data-testid="nav-register-link">
                  <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded px-6 py-2 font-semibold" size="sm">Sign Up</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-4 bg-white" data-testid="mobile-menu">
            <Link to="/" className="block text-slate-700 hover:text-[#8B1818] px-4 py-2" onClick={() => setMobileMenuOpen(false)}>Home</Link>
            <Link to="/applications" className="block text-slate-700 hover:text-[#8B1818] px-4 py-2" onClick={() => setMobileMenuOpen(false)}>Applications</Link>
            <Link to="/products/ebooks" className="block text-slate-700 hover:text-[#8B1818] px-4 py-2" onClick={() => setMobileMenuOpen(false)}>eBooks</Link>
            <Link to="/products/souvenirs" className="block text-slate-700 hover:text-[#8B1818] px-4 py-2" onClick={() => setMobileMenuOpen(false)}>Souvenirs</Link>
            <Link to="/forum" className="block text-slate-700 hover:text-[#8B1818] px-4 py-2" onClick={() => setMobileMenuOpen(false)}>Forum</Link>
            <Link to="/blog" className="block text-slate-700 hover:text-[#8B1818] px-4 py-2" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
            <Link to="/faqs" className="block text-slate-700 hover:text-[#8B1818] px-4 py-2" onClick={() => setMobileMenuOpen(false)}>FAQs</Link>
            {user ? (
              <>
                {user.is_admin && <Link to="/admin" className="block text-slate-700 hover:text-[#8B1818] px-4 py-2" onClick={() => setMobileMenuOpen(false)}>Admin</Link>}
                <Link to="/cart" className="block text-slate-700 hover:text-[#8B1818] px-4 py-2" onClick={() => setMobileMenuOpen(false)}>Cart ({cartCount})</Link>
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="block text-slate-700 hover:text-[#8B1818] text-left px-4 py-2 w-full">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" className="block text-slate-700 hover:text-[#8B1818] px-4 py-2" onClick={() => setMobileMenuOpen(false)}>Login</Link>
                <Link to="/register" className="block text-[#8B1818] font-semibold px-4 py-2" onClick={() => setMobileMenuOpen(false)}>Sign Up</Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;